import React, { useState, useRef } from 'react';
import { Lock, Plus, Check } from 'lucide-react';
import { Input } from '../ui/input';
import PageThumbnailPreview from './PageThumbnailPreview';

const PagesBottomPanel = ({
  show, pages, currentPageId, canvasSize,
  onPageChange, onAddPage,
  onRenamePage, onReorderPages,
}) => {
  const [editingPageId, setEditingPageId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [draggedPageId, setDraggedPageId] = useState(null);
  const scrollRef = useRef(null);

  const handleDragStart = (e, pageId) => {
    setDraggedPageId(pageId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetPageId) => {
    e.preventDefault();
    if (draggedPageId && draggedPageId !== targetPageId) {
      const fromIdx = pages.findIndex(p => p.id === draggedPageId);
      const toIdx = pages.findIndex(p => p.id === targetPageId);
      if (fromIdx !== -1 && toIdx !== -1) {
        const next = [...pages];
        const [removed] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, removed);
        onReorderPages(next);
      }
    }
  };

  const handleDragEnd = () => setDraggedPageId(null);

  // Compute thumbnail dimensions that fit within a compact bounding box
  const MAX_THUMB_HEIGHT = 68;
  const MAX_THUMB_WIDTH = 96;
  const thumbScale = Math.min(MAX_THUMB_HEIGHT / canvasSize.height, MAX_THUMB_WIDTH / canvasSize.width);
  const THUMB_WIDTH = Math.round(canvasSize.width * thumbScale);
  const THUMB_HEIGHT = Math.round(canvasSize.height * thumbScale);
  const LABEL_HEIGHT = 20; // fixed label area: mt-1 (4) + text (~13) + 3 breathing room
  const ITEM_HEIGHT = THUMB_HEIGHT + LABEL_HEIGHT; // uniform total height for all items
  const PANEL_HEIGHT = ITEM_HEIGHT + 32; // items + 16px top + 16px bottom padding

  return (
    <div
      className="bg-white border-t flex-shrink-0 overflow-hidden transition-all duration-200"
      style={{ height: show ? PANEL_HEIGHT : 0 }}
    >
      {show && (
        <div
          ref={scrollRef}
          className="h-full flex items-center gap-3 px-4 overflow-x-auto overflow-y-hidden"
          style={{ scrollbarWidth: 'thin' }}
        >
          {pages.map((page) => {
            const isActive = page.id === currentPageId;
            const isDragging = draggedPageId === page.id;

            return (
              /* Uniform-height card: thumbnail area + label area */
              <div
                key={page.id}
                draggable={!page.locked}
                onDragStart={e => handleDragStart(e, page.id)}
                onDragOver={e => handleDragOver(e, page.id)}
                onDragEnd={handleDragEnd}
                className={`relative flex-shrink-0 flex flex-col cursor-pointer transition-all ${isDragging ? 'opacity-40' : 'opacity-100'}`}
                onClick={() => !editingPageId && onPageChange(page.id)}
                style={{ width: THUMB_WIDTH, height: ITEM_HEIGHT }}
              >
                {/* Thumbnail — fixed height */}
                <div
                  className={`rounded-md overflow-hidden flex-shrink-0 ${isActive ? 'ring-2 ring-purple-600' : 'ring-1 ring-gray-200 hover:ring-purple-400'}`}
                  style={{ height: THUMB_HEIGHT }}
                >
                  <PageThumbnailPreview page={page} canvasSize={canvasSize} thumbWidth={THUMB_WIDTH} />
                </div>

                {/* Lock overlay (informational only) */}
                {page.locked && (
                  <div
                    className="absolute left-0 right-0 top-0 bg-black/25 rounded-md flex items-center justify-center pointer-events-none"
                    style={{ height: THUMB_HEIGHT }}
                  >
                    <Lock className="w-3 h-3 text-white" />
                  </div>
                )}

                {/* Label area — fixed height, always present */}
                <div className="flex-1 flex items-center justify-center mt-1 px-1 overflow-hidden">
                  {editingPageId === page.id ? (
                    <div className="flex items-center gap-1 w-full" onClick={e => e.stopPropagation()}>
                      <Input
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        className="h-5 text-[10px] px-1 py-0"
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') { onRenamePage(page.id, editingName); setEditingPageId(null); }
                          if (e.key === 'Escape') setEditingPageId(null);
                        }}
                      />
                      <button onClick={() => { onRenamePage(page.id, editingName); setEditingPageId(null); }}>
                        <Check className="w-3 h-3 text-green-600" />
                      </button>
                    </div>
                  ) : (
                    <p
                      className="text-[10px] text-gray-600 truncate text-center leading-tight w-full"
                      onDoubleClick={e => { e.stopPropagation(); setEditingPageId(page.id); setEditingName(page.name); }}
                      title={page.name}
                    >
                      {page.name}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {/* Add page button — same total height as thumbnail cards */}
          <div className="flex-shrink-0 flex flex-col" style={{ width: THUMB_WIDTH, height: ITEM_HEIGHT }}>
            <button
              onClick={onAddPage}
              className="flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-gray-300 hover:border-purple-400 hover:bg-purple-50 transition-all text-gray-400 hover:text-purple-600"
              style={{ width: THUMB_WIDTH, height: THUMB_HEIGHT }}
              title="Add new page"
            >
              <Plus className="w-4 h-4" />
              <span className="text-[9px]">Add</span>
            </button>
            {/* Spacer matching label area height so Add aligns with thumbnails */}
            <div style={{ height: LABEL_HEIGHT }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PagesBottomPanel;
